import React from 'react';
import { connect } from 'react-redux';

import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

import LargeButton from '../atoms/LargeButton';
import IconHeading from '../molecules/IconHeading';
import { handleModalClose } from '../../../actions/controls';
import { ArrowRightIcon } from '../../../resources/icons';

import {
  Box,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../../../styles/theme';

const PREFIX = 'Modal';

const classes = {
  form: `${PREFIX}-form`,
  centerDialogActions: `${PREFIX}-centerDialogActions`,
};

const Root = styled('div', {
  shouldForwardProp(propName) {
    return !['centerDialog'].includes(propName);
  },
})(({ theme, centerDialog }) => ({
  [`& .${classes.centerDialogActions}`]: {
    justifyContent: centerDialog && 'center',
  },
}));

const Modal = (props) => {
  const {
    modalName,
    title,
    children,
    icon,
    primaryButtonText,
    secondaryButtonText,
    primaryButtonType = 'primary',
    secondaryButtonType = 'secondary',
    primaryButtonIcon = <ArrowRightIcon />,
    description,
    descriptionSpacing,
    bottomMessage,
    onPrimaryClick,
    disablePrimaryClick = false,
    onSecondaryClick,
    showCloseButton,
    sx,
    centerDialog,
    maxWidth,
    noButtonIcon,
    headingStyle,
    openedModals,
    handleModalClose,
    disableOnClose,
    customWidth,
    dialogBoxPadding,
    dialogBoxMargin,
    noMargin,
  } = props;

  const handlePrimaryClick = async (e) => {
    e.preventDefault();
    try {
      // Check if onPrimaryClick is async by checking if it returns a promise
      const result = onPrimaryClick(e);
      if (result && typeof result.then === 'function') {
        // Wait for the promise to resolve
        await result;
      }
      // Only close modal if no error was thrown
      handleModalClose(modalName);
    } catch (error) {
      // If an error occurs, don't close the modal
    }
  };

  const handleSecondaryClick = (e) => {
    e.preventDefault();
    onSecondaryClick && onSecondaryClick(e);
    handleModalClose(modalName);
  };
  return (
    <Root centerDialog={centerDialog}>
      <Dialog
        maxWidth={maxWidth}
        open={openedModals.includes(modalName)}
        onClose={disableOnClose ? null : () => handleModalClose(modalName)}
        aria-labelledby="form-dialog-title"
        sx={sx}
      >
        <form style={{ margin: theme.spacing(2) }} onSubmit={handlePrimaryClick}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <IconHeading title={title} icon={icon} style_2={headingStyle} />
            {showCloseButton ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => handleModalClose(modalName)}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </DialogTitle>
          <DialogContent>
            {description && <DialogContentText className={descriptionSpacing}>{description}</DialogContentText>}
            {children}
          </DialogContent>
          <DialogActions
            className={classes.centerDialogActions}
            sx={{ paddingBottom: dialogBoxPadding, marginInline: dialogBoxMargin }}
          >
            <Grid container spacing={2}>
              {secondaryButtonText && (
                <Grid item sm={primaryButtonText ? 6 : 12}>
                  <LargeButton
                    buttonType={secondaryButtonType}
                    fullWidth
                    noMargin={noMargin}
                    onClick={handleSecondaryClick}
                    customWidth={customWidth}
                  >
                    {secondaryButtonText}
                  </LargeButton>
                </Grid>
              )}
              {primaryButtonText && (
                <Grid item sm={secondaryButtonText ? 6 : 12}>
                  <LargeButton
                    buttonType={primaryButtonType}
                    color="blue"
                    type="submit"
                    noMargin={noMargin}
                    disabled={disablePrimaryClick}
                    endIcon={!noButtonIcon && primaryButtonIcon}
                    fullWidth
                    onClick={handlePrimaryClick}
                    customWidth={customWidth}
                  >
                    {primaryButtonText}
                  </LargeButton>
                </Grid>
              )}
            </Grid>
          </DialogActions>
          {bottomMessage && (
            <Box
              paddingX={2}
              paddingBottom={2}
              marginTop={-1}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Typography variant="subtitle1" color="warning.dark">
                {bottomMessage}
              </Typography>
            </Box>
          )}
        </form>
      </Dialog>
    </Root>
  );
};

const mapStateToProps = (state) => ({
  openedModals: state.controls.modalOpen,
});

Modal.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  primaryButtonType: PropTypes.string,
  secondaryButtonType: PropTypes.string,
  headingStyle: PropTypes.any,
  icon: PropTypes.element,
  openedModals: PropTypes.array.isRequired,
  modalName: PropTypes.string.isRequired,
  maxWidth: PropTypes.string,
  descriptionSpacing: PropTypes.string,
  showCloseButton: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default connect(mapStateToProps, { handleModalClose })(Modal);
